//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'
import { InferType } from 'yup'
import { ERC20Wei, timestampToUTCDateString, timestampToUTCTimeString } from './conversions'
import {
  DepositTypeToString,
  DynamicDepositStatusEnum,
  yupErc20WeiRequired,
  yupEthAddrOptional,
  yupEthAddrRequired,
  yupTxHashRequired,
} from './Enums'
import { EthAddr, ReportingColumnVisibilityEnum, StringTypeEnum, toFieldMetaData, TxHash } from './SchemaTypes'
import { EventNameForTemplate, EventTypeForTemplate, PoolEventEmailStatus } from './templateApx'
import { ZeroAddress } from 'ethers'

export const DynamicWithdrawalActivityCTOSchema = yup
  .object({
    stage: yup
      .number()
      .required()
      .label('stage')
      .meta(toFieldMetaData({ propName: 'stage', description: 'The current stage of the withdrawal process' })),
    poolAddr: yupEthAddrRequired.label('Vault Address').meta(
      toFieldMetaData({
        propName: 'poolAddr',
        description: 'Address of Vault',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
    lender: yupEthAddrRequired.label('Wallet Address').meta(
      toFieldMetaData({
        propName: 'lender',
        description: 'Wallet Address of Lender',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
    shares: yupErc20WeiRequired.label('Shares').meta(
      toFieldMetaData({
        propName: 'shares',
        description: 'Shares deposited',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),
    assets: yupErc20WeiRequired.label('Assets').meta(
      toFieldMetaData({
        propName: 'assets',
        description: 'Assets deposited',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
    fundsDestination: yupEthAddrRequired.label('Wallet Address').meta(
      toFieldMetaData({
        propName: 'fundsDestination',
        description: 'Wallet Address of fundsDestination ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
    transferRedeem: yup
      .boolean()
      .required()
      .label('Transfer Redeem')
      .meta(
        toFieldMetaData({
          propName: 'transferRedeem',
          description: 'Is it a TransferRedeem',
        })
      ),
    uuid: yup
      .string()
      .required()
      .label('uuid')
      .meta(toFieldMetaData({ propName: 'uuid', description: 'The unique identifier for this withdrawal activity' })),
  })
  .label('WithdrawalActivityCTO')

export interface DynamicWithdrawalActivityCTO {
  poolAddr: EthAddr
  lender: EthAddr
  shares: ERC20Wei
  assets: ERC20Wei
  uuid: string
  fundsDestination: EthAddr
  stage: number
  transferRedeem: boolean
}
export const _testADynamicWithdrawalActivityCTO: DynamicWithdrawalActivityCTO =
  {} as unknown as DynamicWithdrawalActivityCTOInfer
export const _testBDynamicWithdrawalActivityCTO: DynamicWithdrawalActivityCTOInfer =
  {} as unknown as DynamicWithdrawalActivityCTO

type DynamicWithdrawalActivityCTOInfer = InferType<typeof DynamicWithdrawalActivityCTOSchema>
export const DynamicDepositActivityCTOSchema = yup
  .object({
    poolAddr: yupEthAddrRequired
      .label('Vault Address')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'poolAddr',
          description: 'Address of Vault ',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),
    lender: yupEthAddrRequired
      .label('Wallet Address')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'lender',
          description: 'Wallet Address of Lender ',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),
    shares: yupErc20WeiRequired
      .label('Requested Shares')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'shares',
          description: 'Shares deposited',
          stringType: StringTypeEnum.PPT_SHARES,
        })
      ),
    assets: yupErc20WeiRequired
      .label('Requested Assets')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'assets',
          description: 'Assets deposited',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    fundsOrigination: yupEthAddrRequired
      .label('Wallet Address')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'fundsDestination',
          description: 'Wallet Address of fundsDestination ',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),
    txHash: yupTxHashRequired
      .label('txHash')
      .required()
      .meta(toFieldMetaData({ propName: 'txHash', description: 'uuid' })),

    state: yup
      .mixed<DynamicDepositStatusEnum>()
      .oneOf(Object.values(DynamicDepositStatusEnum))
      .label('State')
      .defined()
      .default(DynamicDepositStatusEnum.UNKNOWN)
      .meta(toFieldMetaData({ propName: 'state', description: 'The State of the Deposit' })),
  })
  .label('WithdrawalActivityCTO')

export interface DynamicDepositActivityCTO {
  poolAddr: EthAddr
  lender: EthAddr
  shares: ERC20Wei
  assets: ERC20Wei
  txHash: TxHash
  state: DynamicDepositStatusEnum
  fundsOrigination: EthAddr
}

type DynamicDepositActivityCTOInfer = InferType<typeof DynamicDepositActivityCTOSchema>

export const _testAPoolBaseOverview: DynamicDepositActivityCTO = {} as unknown as DynamicDepositActivityCTOInfer
export const _testBPoolBaseOverview: DynamicDepositActivityCTOInfer = {} as unknown as DynamicDepositActivityCTO

export const WithdrawalActivityCTOSchema = yup
  .object({
    requestedShares: yupErc20WeiRequired.label('Requested Shared').meta(
      toFieldMetaData({
        propName: 'requestedShares',
        description: 'The number of vault tokens requested to be withdrawn',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),
    requestedAssets: yupErc20WeiRequired
      .optional()
      .label('Requested Assets')
      .meta(
        toFieldMetaData({
          propName: 'requestedAssets',
          description: 'The amount of assets requested to be withdrawn',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    requestedPrincipal: yupErc20WeiRequired
      .optional()
      .label('Requested Principal')
      .meta(
        toFieldMetaData({
          propName: 'requestedPrincipal',
          description: 'The amount of principal requested to be withdrawn',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    requestedRepayment: yupErc20WeiRequired
      .optional()
      .label('Requested Repayment')
      .meta(
        toFieldMetaData({
          propName: 'requestedRepayment',
          description: 'The amount of repayment requested to be withdrawn',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    requestTimestamp: yup
      .number()
      .label('Request Timestamp')
      .meta(
        toFieldMetaData({
          propName: 'requestTimestamp',
          description: 'The timestamp when the withdrawal request was made',
        })
      ),
    processedTimestamp: yup
      .number()
      .label('Processed Timestamp')
      .meta(
        toFieldMetaData({
          propName: 'processedTimestamp',
          description: 'The timestamp when the withdrawal request was processed',
        })
      ),
    repaidTimestamp: yup
      .number()
      .label('Repaid Timestamp')
      .meta(
        toFieldMetaData({
          propName: 'repaidTimestamp',
          description: 'The timestamp when the withdrawal was repaid to the user',
        })
      ),
    lender: yupEthAddrRequired.label('Wallet Address').meta(
      toFieldMetaData({
        propName: 'lender',
        description: 'Wallet Address of Lender',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
    loanAddr: yupEthAddrRequired
      .optional()
      .label('Loan Address')
      .meta(
        toFieldMetaData({
          propName: 'loanAddr',
          description: 'The Loan address',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),
    status: yup
      .string()
      .label('Withdraw Status')
      .meta(toFieldMetaData({ propName: 'status', description: 'The current status of the withdrawal request' })),

    eventId: yupErc20WeiRequired
      .optional()
      .label('EventId')
      .meta(
        toFieldMetaData({
          propName: 'eventId',
          description: 'The unique identifier for this withdrawal event',
          stringType: StringTypeEnum.UINT32,
        })
      ),

    transactionHash: yup
      .string()
      .required()
      .label('Transaction Hash')
      .meta(
        toFieldMetaData({
          propName: 'transactionHash',
          stringType: StringTypeEnum.TX_HASH,
          description: 'Hash of the transaction on the blockchain that executed the withdrawal',
        })
      ),
  })
  .label('WithdrawalActivityCTO')

export interface WithdrawalActivityCTO extends InferType<typeof WithdrawalActivityCTOSchema> {}

const PoolEventSchemaValues = {
  id: yup
    .string()
    .label('PoolEventId ')
    .required()
    .meta(toFieldMetaData({ propName: 'id', description: 'ID of event ', stringType: StringTypeEnum.ID })),
  poolAddr: yupEthAddrRequired.label('Vault Address').meta(
    toFieldMetaData({
      propName: 'poolAddr',
      columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
      columnOrder: 6,
      description: 'The smart-contract address of the loan vault. ',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  poolName: yup
    .string()
    .label('Vault Name')
    .default(' ')
    .required()
    .meta(toFieldMetaData({ propName: 'poolName', description: 'Name of the Loan Vault' })),

  poolAddrAccountAddr: yup
    .string()
    .label('pool lender address key')
    .required()
    .default(`${ZeroAddress}-${ZeroAddress}`)
    .meta(
      toFieldMetaData({
        propName: 'poolAddrAccountAddr',
        description: 'The key for the pool lender address',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  eventType: yup
    .mixed<EventTypeForTemplate>()
    .oneOf(Object.values(EventTypeForTemplate))
    .defined()
    .label('Event Label')
    .default(EventTypeForTemplate.Undefined)
    .meta(toFieldMetaData({ propName: 'eventType', description: 'Type of Event' })),

  eventName: yup
    .mixed<EventNameForTemplate>()
    .oneOf(Object.values(EventNameForTemplate))
    .defined()
    .label('Event Name')
    .default(EventNameForTemplate.Undefined)
    .meta(
      toFieldMetaData({
        propName: 'eventName',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 2,
        description: 'Name of Event',
      })
    ),

  currency: yup
    .string()
    .label('Currency Symbol')
    .default(' ')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'currency',
        description: 'The symbol of the currency being used for the event',
      })
    ),
  symbol: yup
    .string()
    .label('Token Symbol')
    .default(' ')
    .required()
    .meta(toFieldMetaData({ propName: 'symbol', description: 'Symbol for Vault Tokens', example: 'OTT' })),

  accountAddr: yupEthAddrRequired
    .label('Lender Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'accountAddr',
        description: 'The Lender address',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  withdrawSequenceId: yup
    .string()
    .label('withdrawSequenceId')
    .meta(toFieldMetaData({ propName: 'withdrawSequenceId', description: 'The sequence ID of the withdrawal' })),
  withdrawStage: yup
    .string()
    .label('withdrawStage')
    .meta(toFieldMetaData({ propName: 'withdrawStage', description: 'The stage of the withdrawal' })),
  loanAddr: yupEthAddrRequired
    .label('Loan Address')
    .optional()
    .meta(
      toFieldMetaData({
        propName: 'loanAddr',
        description: 'The Loan address',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  receiverAddr: yupEthAddrOptional.label('Receiver Address').meta(
    toFieldMetaData({
      propName: 'receiverAddr',
      description: 'The Receiver address',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  senderAddr: yupEthAddrOptional.label('Sender Address').meta(
    toFieldMetaData({
      propName: 'senderAddr',
      description: 'The Sender address',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  priorLoanAddr: yupEthAddrOptional.label('Prior Loan Address').meta(
    toFieldMetaData({
      propName: 'priorLoanAddr',
      description: 'The Prior Loan address',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  blockNumber: yup
    .number()
    .label('Block Number')
    .required()
    .meta(toFieldMetaData({ propName: 'blockNumber', description: 'Block number of the data' })),

  transactionHash: yupTxHashRequired
    .required()
    .label('Transaction Hash')
    .meta(
      toFieldMetaData({
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 1,
        propName: 'transactionHash',
        description: 'Hash of the transaction on the blockchain that executed the event',
        stringType: StringTypeEnum.TX_HASH,
      })
    ),
  timestamp: yup
    .number()
    .label('Timestamp')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'timestamp',
        // columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        // columnOrder: 3,
        description: 'Timestamp of the data',
        stringType: StringTypeEnum.ETH_DATE_TIME,
      })
    ),
  timestampDateString: yup
    .string()
    .required()
    .label('Timestamp Date')
    .default((options) => {
      if (options?.context?.poolEventCTO) {
        return timestampToUTCDateString(options.context.poolEventCTO.timestamp)
      }
      return ' '
    })
    .meta(
      toFieldMetaData({
        propName: 'timestampDateString',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 3.1,
        description: 'Timestamp Date of the data',
      })
    ),
  timestampString: yup
    .string()
    .required()
    .default((options) => {
      if (options?.context?.poolEventCTO) {
        return timestampToUTCTimeString(options.context.poolEventCTO.timestamp)
      }
      return ' '
    })
    .label('Timestamp Date Time')
    .meta(
      toFieldMetaData({
        propName: 'timestampString',
        description: 'Timestamp of the data',
      })
    ),
  transferOutDayTimestamp: yup
    .number()
    .label('transferOutDayTimestamp')
    .meta(
      toFieldMetaData({
        propName: 'transferOutDayTimestamp',
        description: 'The timestamp of the transfer out day',
      })
    ),
  transferInDayTimestamp: yup
    .number()
    .label('transferInDayTimestamp')
    .meta(
      toFieldMetaData({
        propName: 'transferInDayTimestamp',
        description: 'The timestamp of the transfer in day',
      })
    ),
  chainId: yup
    .number()
    .required()
    .label('Chain Id')
    .meta(toFieldMetaData({ propName: 'chainId', example: '5', description: 'ChainId of the Data' })),

  assets: yupErc20WeiRequired
    .label('Assets')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'assets',
        description: 'Amount of assets involved in the event',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 4,
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  expectedAssets: yupErc20WeiRequired
    .label('Expected Assets')
    .required()
    .default('0')
    .meta(
      toFieldMetaData({
        propName: 'expectedAssets',
        description: 'Assets expected to be repaid',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  liquidityTokenSymbol: yup
    .string()
    .label('Liquidity Token Symbol')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'liquidityTokenSymbol',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 7,
        description: 'The symbol of the liquidity token being used for the event',
      })
    ),

  shares: yupErc20WeiRequired
    .label('Vault Tokens')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'shares',
        description: 'The number of vault tokens involved in the event',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 5,
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),

  assetsFromPool: yupErc20WeiRequired.label('assetsFromPool').meta(
    toFieldMetaData({
      propName: 'assetsFromPool',
      description: 'The amount of assets being transferred from the pool to the event',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  assetsFromPriorToNextLoan: yupErc20WeiRequired.label('assetsFromPriorToNextLoan').meta(
    toFieldMetaData({
      propName: 'assetsFromPriorToNextLoan',
      description: 'The amount of assets being transferred from the prior loan to the event',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  assetToReturnToPool: yupErc20WeiRequired.label('assetToReturnToPool').meta(
    toFieldMetaData({
      propName: 'assetToReturnToPool',
      description: 'The amount of assets being transferred from the event to the pool',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  fees: yupErc20WeiRequired.label('fees').meta(
    toFieldMetaData({
      propName: 'fees',
      description: 'The amount of fees being charged for the event',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  principal: yupErc20WeiRequired.label('principal').meta(
    toFieldMetaData({
      propName: 'principal',
      description: 'The amount of principal being transferred from the event',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  repayment: yupErc20WeiRequired.label('repayment').meta(
    toFieldMetaData({
      propName: 'repayment',
      description: 'The amount of principal being repaid to the event',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  liquidityAssetAddr: yupEthAddrRequired.label('Liquidity Asset Address').meta(
    toFieldMetaData({
      propName: 'liquidityAssetAddr',
      description: 'The smart-contract address for the liquidity asset e.g. USDC, EURC. ',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  poolLiquidityAssetBalance: yupErc20WeiRequired.label('poolLiquidityAssetBalance').meta(
    toFieldMetaData({
      propName: 'poolLiquidityAssetBalance',
      description: 'The amount of liquidity assets in the pool',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),

  borrowerVaultLiquidityAssetBalance: yupErc20WeiRequired.label('borrowerVaultLiquidityAssetBalance').meta(
    toFieldMetaData({
      propName: 'borrowerVaultLiquidityAssetBalance',
      description: 'The amount of liquidity assets in the borrower vault',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),

  borrowerWalletLiquidityAssetBalance: yupErc20WeiRequired.label('borrowerWalletLiquidityAssetBalance').meta(
    toFieldMetaData({
      propName: 'borrowerWalletLiquidityAssetBalance',
      description: 'The amount of liquidity assets in the borrower wallet',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),

  feeVaultLiquidityAssetBalance: yupErc20WeiRequired
    .label('feeVaultLiquidityAssetBalance')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'feeVaultLiquidityAssetBalance',
        description: 'The amount of liquidity assets in the fee vault',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  poolAdminLiquidityAssetBalance: yupErc20WeiRequired
    .label('poolAdminLiquidityAssetBalance')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'poolAdminLiquidityAssetBalance',
        description: 'The amount of liquidity assets in the pool admin',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  accountLiquidityAssetBalance: yupErc20WeiRequired
    .label('accountLiquidityAssetBalance')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'accountLiquidityAssetBalance',
        description: 'The amount of liquidity assets in the account',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  accountTokenBalance: yupErc20WeiRequired
    .label('accountTokenBalance')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'accountTokenBalance',
        description: 'The number of vault tokens in the account',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),
  interestAccrued: yupErc20WeiRequired.label('Interest Accrued').meta(
    toFieldMetaData({
      propName: 'interestAccrued',
      description: 'Interest Accrued for the loan at Rollover',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  emailStatus: yup
    .mixed<PoolEventEmailStatus>()
    .oneOf(Object.values(PoolEventEmailStatus))
    .default(PoolEventEmailStatus.Initial)
    .defined()
    .label('Email Status')
    .meta(
      toFieldMetaData({
        propName: 'emailStatus',
        description: 'Status of email sent',
      })
    ),
  depositType: yup
    .number()
    .optional()
    .label('Deposit Type')
    .meta(
      toFieldMetaData({
        propName: 'depositType',
        description: 'The Deposit Type',
        stateToString: DepositTypeToString,
      })
    ),

  transferTxHash: yupTxHashRequired
    .optional()
    .label('Transfer Transaction Hash')
    .meta(
      toFieldMetaData({
        propName: 'transferTxHash',
        stringType: StringTypeEnum.TX_HASH,
        description: 'The transaction hash of the transfer',
      })
    ),
}

export const PoolEventSchema = yup.object(PoolEventSchemaValues)
// .label('PoolEventCTO')
// .meta(toFieldMetaData({ propName: 'PoolEventCTO', description: 'Vault Event' }))

export interface PoolEventCTO {
  id: string
  poolAddr: EthAddr
  poolName: string
  poolAddrAccountAddr: EthAddr
  eventType: EventTypeForTemplate
  eventName: EventNameForTemplate
  currency: string
  symbol: string

  accountAddr: EthAddr
  withdrawSequenceId?: string
  withdrawStage?: string
  loanAddr: EthAddr
  receiverAddr?: EthAddr
  senderAddr?: EthAddr
  priorLoanAddr?: EthAddr
  blockNumber: number
  transactionHash: TxHash
  timestamp: number
  timestampDateString: string
  timestampString: string
  transferOutDayTimestamp?: number
  transferInDayTimestamp?: number
  chainId: number

  expectedAssets: ERC20Wei
  assets: ERC20Wei
  liquidityTokenSymbol: string
  assetsFromPool: ERC20Wei
  assetsFromPriorToNextLoan: ERC20Wei
  assetToReturnToPool: ERC20Wei
  fees: ERC20Wei
  principal: ERC20Wei
  repayment: ERC20Wei
  liquidityAssetAddr: EthAddr
  poolLiquidityAssetBalance: ERC20Wei
  borrowerVaultLiquidityAssetBalance: ERC20Wei
  borrowerWalletLiquidityAssetBalance: ERC20Wei
  feeVaultLiquidityAssetBalance: ERC20Wei
  poolAdminLiquidityAssetBalance: ERC20Wei
  accountLiquidityAssetBalance: ERC20Wei
  accountTokenBalance: ERC20Wei
  interestAccrued: ERC20Wei
  shares: ERC20Wei
  emailStatus: PoolEventEmailStatus
  depositType?: number
  transferTxHash: TxHash
}

type PoolEventCTOInfer = InferType<typeof PoolEventSchema>

export const _testAPoolEvent: PoolEventCTO = {} as unknown as PoolEventCTOInfer
export const _testBPoolEvent: PoolEventCTOInfer = {} as unknown as PoolEventCTO
